import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMatch } from 'react-router';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import Button from '@rio-cloud/rio-uikit/Button';
import ExpanderPanel from '@rio-cloud/rio-uikit/components/expander/ExpanderPanel';
import ContentLoader from '@rio-cloud/rio-uikit/ContentLoader';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';

import { AnalyticsUtils } from '@/configuration/setup/googleAnalytics';
import InternalErrorState from '@/features/app/InternalErrorState';
import { BATTERY_SERVICE_OPEN_REMOTE_DIALOG, sendMessage } from '@/features/app/MessageHandler';
import { useFetchBatteryLevelsQuery, useFetchProductForAssetQuery } from '@/features/battery/api/batterySlice';
import SohBanner from '@/features/battery/SohBanner';
import { getDaysInYearsAndMonths } from '@/features/battery/utils/DateUtils';
import {
    ACTIVE_SOH_STATUSES,
    CRITICAL,
    LOW_MILEAGE,
    NO_DATA,
    OK,
    WARNING,
} from '@/features/batteryOverview/utils/BatteryDataUtils';
import { BatteryDetailsRow } from './BatteryDetailsRow';
import ExpanderTitle from './ExpanderTitle';
import PackLevelView from './PackLevelView';

const BatterySidebar = () => {
    const intl = useIntl();
    const assetId = useMatch('/:assetId')?.params.assetId;
    const [activeTab, setActiveTab] = useState<'system' | 'packs'>('system');
    const { isLoading, isError, error, data: batteryLevel, refetch } = useFetchBatteryLevelsQuery(assetId!);
    const { isLoading: isLoadingProduct, data: products } = useFetchProductForAssetQuery(assetId!);

    const hasServiceCareM = products !== undefined && products.booked_products.includes('SERVICE_CARE_M');

    const systemStateOfHealthStatus =
        batteryLevel?.health_data?.system.state_of_health_status?.toUpperCase() || NO_DATA;
    const sohPercentage = batteryLevel?.health_data?.system.state_of_health_percentage;
    const mileageValue = batteryLevel?.health_data?.system.mileage;
    const fullChargeEnergy = batteryLevel?.health_data?.system.full_charge_energy;
    const dischargingEnergyThroughput = batteryLevel?.health_data?.system.discharging_energy_throughput;
    const usage = getDaysInYearsAndMonths(batteryLevel?.health_data?.system.usage_days, intl);
    const KWH = ' kWh';

    const ExpanderPanelContent = () => (
        <div className="display-grid detail">
            <BatteryDetailsRow name="intl-msg:batteryService.totalMileage" value={mileageValue} unit=" km" />
            <BatteryDetailsRow name="intl-msg:batteryService.batteryAge" value={usage} unit="" />
            {hasServiceCareM && (
                <BatteryDetailsRow
                    name="intl-msg:batteryService.fullChargeEnergy"
                    value={fullChargeEnergy}
                    unit={KWH}
                    tooltipMsg="intl-msg:batteryService.fullChargeEnergy.tooltip"
                />
            )}
            <BatteryDetailsRow
                name="intl-msg:batteryService.dischargedEnergyThroughput"
                value={dischargingEnergyThroughput}
                unit={KWH}
                tooltipMsg="intl-msg:batteryService.dischargedEnergyThroughput.tooltip.body"
            />
        </div>
    );

    if (isLoading || isLoadingProduct) {
        return <LoadingScreen />;
    }

    if (isError) {
        return (
            <div>
                <SidebarTitle />
                <InternalErrorState
                    errorCode={(error as FetchBaseQueryError).status as number}
                    reloadTriggerFunction={refetch}
                    isCustomerFrontend
                />
            </div>
        );
    }

    AnalyticsUtils.gaPush({
        event: 'show_electric_information',
        parameters: {
            element_name: 'BatterySidebar',
            component_name: 'MainNavigation',
            flow_name: 'view_customer_battery_health_data',
            trigger: 'visibility',
        },
        userProps: {},
    });

    // Pack data is only available when the system SoH status is "data complete"
    // (OK / WARNING / CRITICAL). For "Collecting data" (LOW_MILEAGE) or "No data"
    // (NO_DATA) the "Battery packs" tab is disabled.
    const isPackDataAvailable = ACTIVE_SOH_STATUSES.includes(systemStateOfHealthStatus);
    const showPacks = isPackDataAvailable && activeTab === 'packs';

    const systemContent = (
        <>
            {[OK, WARNING, CRITICAL].includes(systemStateOfHealthStatus) ? (
                <>
                    <ExpanderPanel
                        title={
                            <ExpanderTitle
                                percentage={sohPercentage}
                                systemStateOfHealthStatus={systemStateOfHealthStatus}
                            />
                        }
                        titleClassName="width-100pct"
                        headerClassName="width-100pct padding-left-0 padding-top-0 padding-bottom-0"
                        bodyClassName="bg-white"
                        className="border-style-solid border-width-1 border-color-light"
                        onEntered={registerSidebarExpanderEventGA}
                        open={systemStateOfHealthStatus === WARNING || systemStateOfHealthStatus === CRITICAL}
                    >
                        <div className="panel-container">
                            <div className="panel-body padding-0">
                                <ExpanderPanelContent />
                            </div>
                        </div>
                    </ExpanderPanel>
                    <Recommendation systemStateOfHealthStatus={systemStateOfHealthStatus} />
                </>
            ) : (
                <div className="border-style-solid border-width-1 border-color-light panel-container bg-white">
                    <ExpanderTitle percentage={sohPercentage} systemStateOfHealthStatus={systemStateOfHealthStatus} />
                    <div className="panel-body">
                        <ExpanderPanelContent />
                    </div>
                </div>
            )}

            <div className="margin-y-5" />

            {systemStateOfHealthStatus === LOW_MILEAGE && <SohBanner />}

            {hasServiceCareM && (
                <div className="display-flex justify-content-between gap-20 margin-bottom-10">
                    <div className="flex-1-1">
                        <div className="display-flex justify-content-between">
                            <Button
                                onClick={() => {
                                    sendMessage({
                                        type: BATTERY_SERVICE_OPEN_REMOTE_DIALOG,
                                        payload: {
                                            asset_id: assetId,
                                        },
                                    });
                                    registerBatteryHistoryGraphButtonEventGA();
                                }}
                                bsStyle={Button.PRIMARY}
                                variant={Button.VARIANT_LINK}
                                className="text-left"
                            >
                                <span className="rioglyph rioglyph-line-chart" />
                                <span>
                                    <FormattedMessage id="intl-msg:batteryService.batteryAging" />
                                </span>
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return (
        <>
            <SidebarTitle />
            <SidebarNavigation
                activeTab={activeTab}
                onTabChange={setActiveTab}
                packsDisabled={!isPackDataAvailable}
            />
            {showPacks ? <PackLevelView assetId={assetId!} hasServiceCareM={hasServiceCareM} /> : systemContent}
        </>
    );
};

const SidebarTitle = () => (
    <div className="h6 text-uppercase text-color-dark margin-top-0">
        <FormattedMessage id="intl-msg:batteryService.batterySidebar.titleShort" />
    </div>
);

const SidebarNavigation = ({
    activeTab,
    onTabChange,
    packsDisabled,
}: {
    activeTab: 'system' | 'packs';
    onTabChange: (tab: 'system' | 'packs') => void;
    packsDisabled: boolean;
}) => {
    const packsButton = (
        <Button
            asToggle
            active={activeTab === 'packs'}
            disabled={packsDisabled}
            bsStyle={Button.PRIMARY}
            variant={activeTab === 'packs' ? undefined : Button.VARIANT_OUTLINE}
            onClick={() => onTabChange('packs')}
        >
            <FormattedMessage id="intl-msg:batteryService.navigation.batteryPacks" />
        </Button>
    );

    return (
        <div className="btn-group margin-bottom-15">
            <Button
                asToggle
                active={activeTab === 'system'}
                bsStyle={Button.PRIMARY}
                variant={activeTab === 'system' ? undefined : Button.VARIANT_OUTLINE}
                onClick={() => onTabChange('system')}
            >
                <FormattedMessage id="intl-msg:batteryService.navigation.batterySystem" />
            </Button>
            {packsDisabled ? (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id="battery-packs-disabled-tooltip" width="auto">
                            <FormattedMessage id="intl-msg:batteryService.navigation.batteryPacks.tooltip" />
                        </Tooltip>
                    }
                >
                    {/* span wrapper so the tooltip still triggers on a disabled button */}
                    <span className="display-inline-block" data-testid="battery-packs-disabled">
                        {packsButton}
                    </span>
                </OverlayTrigger>
            ) : (
                packsButton
            )}
        </div>
    );
};

const LoadingScreen = () => {
    const marginTopBottom10 = 'margin-top-10 margin-bottom-10';

    return (
        <div className="col-xs-12 col-sm-6 col-md-4 display-flex flex-column">
            <ContentLoader height={35} className={marginTopBottom10} />
            <ContentLoader height={100} width="100%" className={marginTopBottom10} />
            <ContentLoader height={100} width="100%" className={marginTopBottom10} />
        </div>
    );
};

const Recommendation = ({ systemStateOfHealthStatus }: { systemStateOfHealthStatus: string }) => {
    if (![WARNING, CRITICAL].includes(systemStateOfHealthStatus)) {
        return <></>;
    }

    const className = systemStateOfHealthStatus.toLowerCase();
    return (
        <div className={`panel panel-default border-color-${className === 'critical' ? 'danger' : className}`}>
            <div className={`soh-bg-${className}`}>
                <div className="panel-body">
                    <div className="text-color-black">
                        <div className={'h5'}>
                            <span className={`rioglyph rioglyph-info-sign margin-right-5 text-color-${className}`} />
                            <FormattedMessage id="intl-msg:batteryService.stateOfHealth.recommendation.title" />
                        </div>
                        <div className="margin-left-25">
                            <FormattedMessage
                                id={`intl-msg:batteryService.stateOfHealth.recommendation.${className}`}
                                values={{
                                    b: (chunks) => <strong>{chunks}</strong>,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const registerSidebarExpanderEventGA = () => {
    AnalyticsUtils.gaPush({
        event: 'click_battery_sidebar_expander',
        parameters: {
            element_name: 'BatterySidebar',
            component_name: 'Expander',
            flow_name: 'view_customer_battery_health_data',
            trigger: 'click',
        },
        userProps: {},
    });
};

const registerBatteryHistoryGraphButtonEventGA = () => {
    AnalyticsUtils.gaPush({
        event: 'click_battery_history_graph_button',
        parameters: {
            element_name: 'BatterySidebar',
            component_name: 'Button',
            flow_name: 'view_customer_battery_health_data',
            trigger: 'click',
        },
        userProps: {},
    });
};

export default BatterySidebar;
